import type { WashedLocale } from '@washed/i18n';
import { PageShell } from './PageShell.js';
import styles from './SeoIntentPage.module.css';

const SITE = 'https://washedafrica.com';

const COPY = {
  fr: {
    path: '/etude-lessive-lome',
    eyebrow: 'Washed · Note de recherche',
    title: 'Comment Washed a étudié la lessive à Lomé',
    intro:
      'Pour préparer le service, Washed a utilisé un questionnaire de recherche terrain pour mieux comprendre comment les foyers de Lomé organisent déjà la lessive à domicile. Le jeu de données existant compte 63 réponses. Cette page explique ce que nous avons demandé, pourquoi, et les limites de ce que ces réponses permettent d’affirmer.',
    facts: [
      ['Réponses', '63'],
      ['Questionnaire', 'Jusqu’à 10 questions'],
      ['Langues', 'Français + anglais'],
      ['Objet', 'Lessive à domicile à Lomé'],
    ],
    sections: [
      {
        heading: 'Ce que nous voulions comprendre',
        paragraphs: [
          'Le questionnaire ne demandait pas simplement si les gens « aimaient » une idée. Il commençait par le comportement actuel : présence ou non d’une laveuse à domicile, fréquence de ses visites, montant payé par mois et personnes du foyer pour lesquelles elle lave.',
          'Il demandait ensuite quelles frictions existaient déjà dans cette relation : manque de fiabilité, disparition ou absence, qualité du lavage, changements de prix, linge perdu ou abîmé, ou absence de problème particulier. La taille du foyer et le quartier étaient également demandés.',
        ],
      },
      {
        heading: 'Les prix testés dans le questionnaire',
        paragraphs: [
          'Deux questions testaient explicitement la disposition à payer : 2 500 XOF par mois pour une visite et 4 500 XOF par mois pour deux visites. Ces montants étaient des points de test de recherche, pas une promesse de prix final.',
          'Les tarifs actuellement publiés par Washed sont différents : Solo 1× est à 4 500 XOF par mois pour une visite et Solo 2× à 8 500 XOF pour deux visites. Nous distinguons volontairement les prix testés dans la recherche des prix réellement proposés aujourd’hui.',
        ],
      },
      {
        heading: 'Un questionnaire avec branchement',
        paragraphs: [
          'Le questionnaire comporte jusqu’à dix questions. Lorsqu’une personne indique qu’elle n’a pas de laveuse à domicile et lave elle-même, les questions qui supposent une relation existante avec une laveuse sont sautées ; le parcours comporte alors six questions.',
          'Ce branchement évite de forcer des réponses hypothétiques sur la fréquence, le prix ou les frustrations d’une relation que la personne n’a pas. Le quartier est demandé pour aider à comprendre où se situe la demande potentielle et à prioriser le lancement.',
        ],
      },
      {
        heading: 'Ce que 63 réponses ne prouvent pas',
        paragraphs: [
          'Il s’agit d’une recherche exploratoire et non d’un échantillon représentatif de toute la population de Lomé. Le nombre de réponses, le mode de recrutement et l’auto-sélection des participants signifient qu’on ne doit pas transformer ce questionnaire en estimation automatique de part de marché ou de comportement de toute la ville.',
          'Nous ne publions pas ici de pourcentages que nous n’avons pas vérifiés dans une synthèse agrégée. Cette page documente la méthode et les questions réellement posées ; elle ne fabrique pas de conclusions statistiques à partir du simple fait que 63 personnes ont répondu.',
        ],
      },
      {
        heading: 'Pourquoi cette recherche a compté dans la conception de Washed',
        paragraphs: [
          'Le produit a été conçu autour d’un comportement déjà familier à Lomé : une personne vient au domicile pour faire la lessive. Washed ajoute une couche d’organisation à cette relation — planification, paiement, continuité avec la laveuse habituelle et remplacement lorsque nécessaire — plutôt que d’imposer un modèle de laverie ou de collecte.',
          'La recherche a surtout servi à tester les bonnes questions : fréquence réelle du besoin, niveau de dépense, confiance, taille du foyer, quartier et sensibilité au prix. Ce sont ces dimensions que le produit doit continuer à mesurer pendant le bêta payant.',
        ],
      },
    ],
    faq: [
      {
        q: 'Est-ce que 63 réponses suffisent pour mesurer le marché de Lomé ?',
        a: 'Non. C’est un signal de recherche utile, mais pas un échantillon représentatif permettant d’extrapoler automatiquement à toute la ville.',
      },
      {
        q: 'Pourquoi ne pas publier les pourcentages maintenant ?',
        a: 'Parce que cette page documente uniquement les éléments vérifiables dans le produit actuel. Les résultats agrégés doivent être contrôlés avant d’être présentés comme statistiques publiques.',
      },
      {
        q: 'Le questionnaire testait-il les tarifs actuels ?',
        a: 'Non. Il testait 2 500 XOF pour une visite et 4 500 XOF pour deux visites. Le tarif Solo 1× actuellement publié est de 4 500 XOF, et le catalogue actuel comporte six formules.',
      },
      {
        q: 'Peut-on encore répondre au sondage ?',
        a: 'Oui. Le questionnaire Washed reste accessible depuis la page Sondage tant que la collecte de recherche est ouverte.',
      },
    ],
    related: [
      ['/sondage', 'Voir le questionnaire'],
      ['/tarifs', 'Voir les tarifs actuels'],
      ['/comment-ca-marche', 'Comprendre le service'],
      ['/zones', 'Quartiers desservis'],
    ],
  },
  en: {
    path: '/en/lome-laundry-research',
    eyebrow: 'Washed · Research note',
    title: 'How Washed researched laundry in Lomé',
    intro:
      'To shape the service, Washed used a field-research questionnaire to better understand how households in Lomé already organize in-home laundry. The existing dataset contains 63 responses. This page documents what we asked, why we asked it, and the limits of what those responses can support.',
    facts: [
      ['Responses', '63'],
      ['Questionnaire', 'Up to 10 questions'],
      ['Languages', 'French + English'],
      ['Topic', 'In-home laundry in Lomé'],
    ],
    sections: [
      {
        heading: 'What we wanted to understand',
        paragraphs: [
          'The questionnaire did not simply ask whether people “liked” an idea. It started with current behavior: whether a washerwoman already visits the home, how often she comes, how much the household pays per month and who in the household she washes for.',
          'It then asked about existing friction in that relationship: unreliability, disappearing or missing visits, washing quality, price changes, lost or damaged clothes, or no particular problem. Household size and neighbourhood were also collected.',
        ],
      },
      {
        heading: 'The prices tested in the questionnaire',
        paragraphs: [
          'Two questions explicitly tested willingness to pay: 2,500 XOF per month for one visit and 4,500 XOF per month for two visits. Those were research price points, not a promise of final pricing.',
          'Washed’s currently published pricing is different: Solo 1× is 4,500 XOF per month for one visit and Solo 2× is 8,500 XOF for two visits. We intentionally distinguish research price tests from the prices customers are actually shown today.',
        ],
      },
      {
        heading: 'A branching questionnaire',
        paragraphs: [
          'The questionnaire contains up to ten questions. When someone says they do not currently have a washerwoman and wash for themselves, questions that assume an existing washerwoman relationship are skipped; that path contains six questions.',
          'This branching avoids forcing hypothetical answers about frequency, price or frustrations with a relationship the respondent does not have. Neighbourhood is collected to help understand where potential demand is located and inform rollout prioritization.',
        ],
      },
      {
        heading: 'What 63 responses do not prove',
        paragraphs: [
          'This is exploratory research, not a representative sample of the entire population of Lomé. Response count, recruitment method and participant self-selection mean the questionnaire should not automatically be converted into a citywide market-share or behavior estimate.',
          'We do not publish percentages here that have not been verified in an aggregated analysis. This page documents the method and the questions actually asked; it does not manufacture statistical conclusions from the fact that 63 people responded.',
        ],
      },
      {
        heading: 'Why the research mattered to the Washed model',
        paragraphs: [
          'The product was designed around a behavior already familiar in Lomé: someone comes to the home to do laundry. Washed adds organization to that relationship — scheduling, payment, continuity with the regular washerwoman and replacement when needed — rather than imposing a laundromat or pickup model.',
          'The research primarily helped test the right dimensions: real service frequency, spending level, trust, household size, neighbourhood and price sensitivity. Those are also the dimensions the paid beta needs to keep measuring.',
        ],
      },
    ],
    faq: [
      {
        q: 'Are 63 responses enough to size the Lomé market?',
        a: 'No. They are a useful research signal, but not a representative sample that can automatically be extrapolated to the entire city.',
      },
      {
        q: 'Why not publish the percentages now?',
        a: 'Because this page only documents claims that are verifiable in the current product and research materials. Aggregated results should be checked before being presented as public statistics.',
      },
      {
        q: 'Did the questionnaire test the current prices?',
        a: 'No. It tested 2,500 XOF for one visit and 4,500 XOF for two visits. The currently published Solo 1× price is 4,500 XOF, and the current catalogue has six plans.',
      },
      {
        q: 'Can people still take the survey?',
        a: 'Yes. The Washed questionnaire remains accessible from the Survey page while research collection is open.',
      },
    ],
    related: [
      ['/sondage', 'View the questionnaire'],
      ['/en/tarifs', 'See current pricing'],
      ['/en/comment-ca-marche', 'How the service works'],
      ['/en/zones', 'Service areas'],
    ],
  },
} as const;

interface ResearchPageProps {
  locale: WashedLocale;
  onLocaleChange: (locale: WashedLocale) => void;
}

export function researchPageFromPath(path: string): boolean {
  const normalized = path.replace(/\/+$/u, '') || '/';
  return normalized === '/etude-lessive-lome' || normalized === '/lome-laundry-research';
}

export function ResearchPage({ locale, onLocaleChange }: ResearchPageProps) {
  const c = COPY[locale];
  const pageUrl = `${SITE}${c.path}`;
  const structured = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${pageUrl}#article`,
    headline: c.title,
    description: c.intro,
    datePublished: '2026-09-11',
    dateModified: '2026-09-11',
    inLanguage: locale,
    mainEntityOfPage: pageUrl,
    author: { '@id': `${SITE}/#organization` },
    publisher: { '@id': `${SITE}/#organization` },
    about: ['Lomé', 'Togo', 'in-home laundry', 'washerwoman services'],
  };

  return (
    <PageShell
      locale={locale}
      onLocaleChange={onLocaleChange}
      eyebrow={c.eyebrow}
      title={c.title}
      intro={c.intro}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structured) }}
      />

      <section
        className={styles.facts}
        aria-label={locale === 'fr' ? 'Méthode en bref' : 'Method at a glance'}
      >
        {c.facts.map(([label, value]) => (
          <div key={label} className={styles.fact}>
            <span className={styles.factLabel}>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </section>

      <div className={styles.article}>
        {c.sections.map((section) => (
          <section key={section.heading} className={styles.section}>
            <h2>{section.heading}</h2>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </section>
        ))}
      </div>

      <section className={styles.faqBlock} aria-labelledby="research-faq">
        <h2 id="research-faq">
          {locale === 'fr' ? 'Questions sur la recherche' : 'Research questions'}
        </h2>
        <dl className={styles.faqList}>
          {c.faq.map((item) => (
            <div key={item.q} className={styles.faqItem}>
              <dt>{item.q}</dt>
              <dd>{item.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <aside className={styles.related} aria-label={locale === 'fr' ? 'Continuer' : 'Continue'}>
        <h2>{locale === 'fr' ? 'Continuer' : 'Continue'}</h2>
        <div className={styles.relatedGrid}>
          {c.related.map(([href, label]) => (
            <a key={href} href={href} className={styles.relatedCard}>
              <strong>{label}</strong>
              <span>{locale === 'fr' ? 'Ouvrir' : 'Open'} →</span>
            </a>
          ))}
        </div>
      </aside>
    </PageShell>
  );
}
